import { ReactNode, useState } from "react";
import { XIcon } from "@phosphor-icons/react";
import { AppButton, ClickAwayListener } from "@libs/zapi-ui/custom";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { ChatFab } from "./ChatFab";
import { dockedChatPanelWidthClasses } from "./ResponsiveChatPanel";

type TDockedChatIslandProps = {
  children: ReactNode;
  hasUnread?: boolean;
  hideFab?: boolean;
  onOpenChange?: (open: boolean) => void;
  testId?: string;
  // The language of the conversation inside, which can differ from the interface language. Set so
  // the browser draws Japanese text with Japanese glyph forms rather than Chinese ones.
  lang?: string;
  // Selectors for controls that should NOT dismiss the open island when tapped (e.g. the
  // Project Setup / Case Frame tabs), so navigating between tabs keeps the chat open on mobile.
  keepOpenSelectors?: string[];
};

// Chat shell shared by the project details page and the create-project flow:
// docked at `hd` and wider, below that a FAB opening a slide-in island.
// Children stay mounted while closed so the chat keeps running in the background.
export function DockedChatIsland({
  children,
  hasUnread = false,
  hideFab = false,
  onOpenChange,
  testId,
  lang,
  keepOpenSelectors
}: TDockedChatIslandProps) {
  const isTablet = useMediaQuery("(max-width: 984px)");
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  return (
    <>
      {!isOpen && !hideFab && (
        <ChatFab hasUnread={hasUnread} onClick={() => handleOpenChange(true)} className="z-20 hd:hidden" />
      )}
      <ClickAwayListener
        insideSelectors={keepOpenSelectors}
        onClickAway={() => {
          if (isOpen) handleOpenChange(false);
        }}
      >
        <div
          data-testid={testId}
          lang={lang}
          className={cn(
            "fixed top-0 z-20 border-l border-innoveraZinc-200 h-[100svh] tablet:h-full w-full bg-white",
            "mobile:max-w-[340px] hd:shrink-0 min-w-0 overflow-hidden",
            dockedChatPanelWidthClasses,
            "hd:static hd:z-auto hd:border-none hd:bottom-auto hd:top-0 transition-all duration-300",
            isOpen ? "right-0" : "right-[-100%]"
          )}
        >
          {isTablet && (
            <AppButton
              appearance="text"
              size="icon"
              onClick={() => handleOpenChange(false)}
              aria-label="Close chat"
              className="absolute top-3 right-3 z-10"
            >
              <XIcon />
            </AppButton>
          )}
          {children}
        </div>
      </ClickAwayListener>
    </>
  );
}
